import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { LOGO_URL } from "@/lib/api";
import {
  LayoutDashboard, Package, Users, Truck, LogOut, Menu, X, MapPin, Inbox, MessageSquare, Warehouse,
} from "lucide-react";

const NAV = {
  admin: [
    { to: "/", label: "Panel", icon: LayoutDashboard },
    { to: "/pickups", label: "Recogidas", icon: Inbox },
    { to: "/shipments", label: "Envíos", icon: Package },
    { to: "/warehouse", label: "Almacén", icon: Warehouse },
    { to: "/addresses", label: "Direcciones", icon: MapPin },
    { to: "/chat", label: "Chat", icon: MessageSquare },
    { to: "/users", label: "Usuarios", icon: Users },
  ],
  driver: [
    { to: "/", label: "Panel", icon: LayoutDashboard },
    { to: "/shipments", label: "Mis Rutas", icon: Package },
  ],
  client: [
    { to: "/", label: "Panel", icon: LayoutDashboard },
    { to: "/shipments", label: "Mis Envíos", icon: Package },
    { to: "/addresses", label: "Direcciones", icon: MapPin },
    { to: "/chat", label: "Chat", icon: MessageSquare },
  ],
};

const ROLE_LABEL = { admin: "Administrador", driver: "Conductor", client: "Cliente" };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const items = NAV[user.role] || [];

  useEffect(() => {
    let active = true;
    const fetchUnread = () => api.get("/chat/unread").then((r) => active && setChatUnread(r.data.count)).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 10000);
    return () => { active = false; clearInterval(t); };
  }, [loc.pathname]);

  const doLogout = async () => { await logout(); nav("/login"); };

  const NavLinks = ({ onClick }) => (
    <>
      {items.map((it) => {
        const active = loc.pathname === it.to;
        const Icon = it.icon;
        return (
          <Link
            key={it.to}
            to={it.to}
            onClick={onClick}
            data-testid={`nav-${it.to === "/" ? "home" : it.to.slice(1)}`}
            className={`flex items-center gap-3 px-4 py-3 text-sm font-bold uppercase tracking-wide border-l-4 transition-colors ${
              active
                ? "border-freight bg-freight/10 text-freight"
                : "border-transparent text-neutral-400 hover:text-white hover:bg-neutral-800"
            }`}
          >
            <Icon className="w-5 h-5" strokeWidth={2.4} />
            <span className="flex-1">{it.label}</span>
            {it.to === "/chat" && chatUnread > 0 && (
              <span data-testid="chat-badge" className="bg-freight text-black text-[10px] font-bold px-1.5 rounded-full">{chatUnread}</span>
            )}
          </Link>
        );
      })}
    </>
  );

  const Brand = () => (
    <div className="flex items-center gap-2 px-4 h-16 border-b border-neutral-800">
      <img src={LOGO_URL} alt="Triple M" className="w-10 h-10 bg-white p-0.5 object-contain" />
      <div className="leading-none">
        <div className="font-display font-black text-white text-lg tracking-tighter">TRIPLE M</div>
        <div className="font-mono text-[8px] tracking-[0.2em] text-freight">SERVICIOS LOGÍSTICOS</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 bg-[#0A0A0A] fixed inset-y-0 left-0 z-40">
        <Brand />
        <nav className="flex-1 py-4">
          <NavLinks />
        </nav>
        <div className="border-t border-neutral-800 p-4">
          <div className="mb-3">
            <div className="text-white font-semibold text-sm truncate">{user.name}</div>
            <div className="font-mono text-[10px] text-freight uppercase tracking-wider">{ROLE_LABEL[user.role]}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            className="flex items-center gap-2 text-neutral-400 hover:text-white text-sm font-bold uppercase tracking-wide transition-colors"
          >
            <LogOut className="w-4 h-4" /> Salir
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 inset-x-0 h-14 bg-[#0A0A0A] z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="Triple M" className="w-8 h-8 bg-white p-0.5 object-contain" />
          <span className="font-display font-black text-white tracking-tighter">TRIPLE M</span>
        </div>
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)} className="text-white">
          <Menu className="w-6 h-6" />
        </button>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-[#0A0A0A] flex flex-col animate-slide-up">
            <div className="flex items-center justify-between">
              <Brand />
              <button onClick={() => setOpen(false)} className="text-white pr-4"><X className="w-6 h-6" /></button>
            </div>
            <nav className="flex-1 py-4"><NavLinks onClick={() => setOpen(false)} /></nav>
            <div className="border-t border-neutral-800 p-4">
              <div className="mb-3">
                <div className="text-white font-semibold text-sm">{user.name}</div>
                <div className="font-mono text-[10px] text-freight uppercase">{ROLE_LABEL[user.role]}</div>
              </div>
              <button onClick={doLogout} className="flex items-center gap-2 text-neutral-400 text-sm font-bold uppercase">
                <LogOut className="w-4 h-4" /> Salir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom nav mobile */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 h-16 bg-[#0A0A0A] z-40 flex items-center justify-around border-t border-neutral-800">
        {items.map((it) => {
          const active = loc.pathname === it.to;
          const Icon = it.icon;
          return (
            <Link key={it.to} to={it.to} data-testid={`bottomnav-${it.to === "/" ? "home" : it.to.slice(1)}`}
              className={`flex flex-col items-center gap-0.5 px-3 ${active ? "text-freight" : "text-neutral-500"}`}>
              <Icon className="w-5 h-5" strokeWidth={2.4} />
              <span className="text-[9px] font-bold uppercase">{it.label.split(" ")[0]}</span>
            </Link>
          );
        })}
      </nav>

      <main className="flex-1 md:ml-64 pt-14 md:pt-0 pb-20 md:pb-0 min-h-screen">
        {children}
      </main>
    </div>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tighter">{title}</h1>
        {subtitle && <p className="text-muted-foreground text-sm mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
